import cx from 'classnames'
import { motion } from 'motion/react'
import { FC, useMemo } from 'react'
import { ActionButtonProps } from 'src/components/ActionButton'
import { PictureProps } from 'src/components/Picture'
import { RichTextProps } from 'src/components/RichText'
import {
  scaleRevealBottomLeft,
  scaleRevealTopRight,
  separatorReveal,
  slideReveal,
  motionGroup,
} from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { MotionMask, MotionWrapper, Separator } from 'src/theme/styled'

import * as SC from './styled'

export type BigPushProps = MinimalSeoBlock & {
  className?: string
  color?: string
  title?: string
  pictureProps?: PictureProps
  richTextProps?: RichTextProps
  actionButtonProps?: ActionButtonProps
}

const BigPush: FC<BigPushProps> = ({
  className,
  htmlTitleTag,
  color = colors.yellowPale,
  title,
  pictureProps,
  richTextProps,
  actionButtonProps,
}) => {
  const { motions, variants } = useBigPushMotion()

  const Title = motion.create(SC.Title)

  return (
    <SC.Container className={cx('BigPush', className)} {...motions.container}>
      {pictureProps && (
        <SC.PictureWrapper $color={color}>
          <SC._Picture {...pictureProps} variants={variants.picture} />
        </SC.PictureWrapper>
      )}
      <SC.Wrapper $color={color}>
        {title && (
          <MotionMask>
            <Title as={htmlTitleTag} variants={variants.title}>
              {title}
            </Title>
          </MotionMask>
        )}
        <Separator role="separator" aria-hidden variants={variants.separator} />
        <SC.Content>
          {richTextProps && (
            <MotionMask>
              <MotionWrapper variants={variants.text}>
                <SC._RichText {...richTextProps} />
              </MotionWrapper>
            </MotionMask>
          )}
          {actionButtonProps && (
            <MotionMask>
              <MotionWrapper variants={variants.action}>
                <SC._ActionButton {...actionButtonProps} />
              </MotionWrapper>
            </MotionMask>
          )}
        </SC.Content>
      </SC.Wrapper>
      <SC.Decoration $color={color} aria-hidden />
      <SC.ContentBG $color={color} aria-hidden variants={variants.background} />
    </SC.Container>
  )
}

const useBigPushMotion = () => {
  const motions = useMemo(
    () => ({
      container: motionGroup({}).motionProps,
    }),
    []
  )

  const variants = useMemo(
    () => ({
      background: scaleRevealBottomLeft({ duration: 1.3 }, 'ease'),
      picture: scaleRevealTopRight({ duration: 1.3 }, 'ease'),
      separator: separatorReveal({ delay: 0.4 }),
      title: slideReveal({ y: '120%', delay: 0.5 }),
      text: slideReveal({ delay: 0.6 }),
      action: slideReveal({ delay: 0.7 }),
    }),
    []
  )

  return { motions, variants }
}

export default BigPush
